import type {
  CatalogListItem,
  CatalogVariationItem,
} from '@skupreme/api-contracts/core';
import { DotsThreeIcon } from '@phosphor-icons/react';
import {
  Badge,
  Button,
  Cell,
  Image,
  Menu,
  Row,
  RowExpandButton,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCatalogVariations } from '../../hooks/use-catalog-variations';
import type { CatalogItemNavigation } from '../../lib/item-navigation';
import {
  extractMarketplaceErrors,
  getConfiguredMarketplaces,
} from '../../legacy-bridge';
import { InventoryCell } from './InventoryCell';
import { ItemProductCell, type MarketplaceErrors } from './ItemTitleCell';
import { MarketplaceIcons } from './MarketplaceIcons';
import { PriceCell } from './PriceCell';

type CatalogItem =
  | CatalogListItem
  | (CatalogVariationItem &
      Partial<Pick<CatalogListItem, 'tags' | 'is_duplicate_listing'>>);

export type CatalogRowContext = {
  /** The settled search: a parent opens on its own while one is set. */
  search: string;
  regionCurrency?: string;
  readOnly?: boolean;
  onToggleSync: (
    item: CatalogItem,
    marketplaceKey: string,
    marketplace: unknown,
    parentId?: string,
  ) => void;
  onResync: (item: CatalogItem) => void;
  /** Where the list sits, so an opened item can close back to it. */
  listUrl?: string;
};

export const CATALOG_COLUMN_COUNT = 6;

const detailPath = (id: string) =>
  `/app/catalogs/catalog/${encodeURIComponent(id)}`;

function useMarketplaceErrors(item: CatalogItem): MarketplaceErrors {
  return useMemo(() => {
    if (!CommonUtility.isValidObject(item?.marketplaces)) return [];

    return extractMarketplaceErrors(item, getConfiguredMarketplaces(item));
  }, [item]);
}

export function Tags({ tags }: { tags?: string[] }) {
  if (!tags?.length) return null;

  return (
    <div className="sk:flex sk:flex-wrap sk:gap-1">
      {tags.map(tag => (
        <Badge key={tag}>{tag}</Badge>
      ))}
    </div>
  );
}

function Marketplaces({
  item,
  context,
  parentId,
}: {
  item: CatalogItem;
  context: CatalogRowContext;
  parentId?: string;
}) {
  return (
    <MarketplaceIcons
      marketplaces={item.marketplaces}
      readOnly={context.readOnly}
      onToggleSync={(marketplaceKey, marketplace) =>
        context.onToggleSync(item, marketplaceKey, marketplace, parentId)
      }
    />
  );
}

function Actions({
  item,
  context,
}: {
  item: CatalogItem;
  context: CatalogRowContext;
}) {
  const canResync =
    !context.readOnly && CommonUtility.isValidObject(item.marketplaces?.tiktok);

  if (!canResync) {
    return null;
  }

  return (
    <Menu
      aria-label="Listing actions"
      trigger={
        <Button
          variant="ghost"
          shape="circle"
          aria-label="Listing actions"
          className="sk:size-6 sk:opacity-0 sk:group-hover:opacity-100 sk:focus-visible:opacity-100 sk:aria-expanded:opacity-100"
        >
          <DotsThreeIcon size={16} weight="bold" />
        </Button>
      }
      actions={[{ id: 'resync', label: 'Re-Sync to TikTok' }]}
      onAction={() => context.onResync(item)}
    />
  );
}

/** A listing that is not a parent: on its own, or as a parent's variation. */
function ItemRow({
  item,
  context,
  parentId,
  siblingIds,
}: {
  item: CatalogItem;
  context: CatalogRowContext;
  parentId?: string;
  /** What an opened item steps through: the page's rows, or its parent's variations. */
  siblingIds: string[];
}) {
  const navigate = useNavigate();
  const marketplaceErrors = useMarketplaceErrors(item);
  const navigation: CatalogItemNavigation | undefined = context.listUrl
    ? { itemIds: siblingIds, listUrl: context.listUrl }
    : undefined;

  return (
    <Row
      id={parentId ? `${parentId}/${item._id}` : item._id}
      isNested={!!parentId}
      onAction={() => navigate(detailPath(item._id), { state: navigation })}
    >
      <Cell>
        <ItemProductCell
          item={item}
          imageUrl={item.p_images?.display_image}
          marketplaceErrors={marketplaceErrors}
          linkState={navigation}
        />
      </Cell>
      <Cell className="sk:whitespace-nowrap">
        <PriceCell item={item} regionCurrency={context.regionCurrency} />
      </Cell>
      <Cell>
        <Tags tags={item.tags} />
      </Cell>
      <Cell>
        <InventoryCell item={item} />
      </Cell>
      <Cell>
        <Marketplaces item={item} context={context} parentId={parentId} />
      </Cell>
      <Cell className="sk:text-right">
        <Actions item={item} context={context} />
      </Cell>
    </Row>
  );
}

function firstImage(images: unknown): string | undefined {
  const [first] = Array.isArray(images) ? images : [];

  return typeof first === 'string' ? first : undefined;
}

function PlaceholderRow({ id }: { id: string }) {
  return (
    <Row id={id} isNested>
      <Cell colSpan={CATALOG_COLUMN_COUNT}>
        <div className="sk:h-4 sk:w-1/3 sk:animate-pulse sk:rounded-option sk:bg-fill-secondary" />
      </Cell>
    </Row>
  );
}

/** A parent and, while it is open, its variations right under it. */
function ParentRows({
  item,
  context,
}: {
  item: CatalogListItem;
  context: CatalogRowContext;
}) {
  const [isExpanded, setExpanded] = useState(!!context.search);

  useEffect(() => {
    setExpanded(!!context.search);
  }, [context.search]);

  const variations = useCatalogVariations(item._id, context.search, isExpanded);
  const variationIds = useMemo(
    () => (variations.data ?? []).map(child => child._id),
    [variations.data],
  );

  return (
    <>
      {/* The whole row opens and closes the variations; the caret is its visible handle. */}
      <Row id={item._id} onAction={() => setExpanded(!isExpanded)}>
        <Cell>
          <div className="sk:flex sk:items-center sk:gap-3">
            <Image
              src={firstImage(item.variation_images)}
              alt=""
              size="md"
              fit="contain"
            />
            <div className="sk:flex sk:min-w-0 sk:flex-col">
              <span className="sk:flex sk:items-center sk:gap-1.5">
                {item.short_title || item.p_title}
                {item.is_bundle ? <Badge tone="accent">Kit</Badge> : null}
              </span>
              <span className="sk:text-xs sk:text-fg-tertiary">
                {item.total_variations || 0} variations
              </span>
            </div>
            <RowExpandButton
              isExpanded={isExpanded}
              onExpandedChange={setExpanded}
              aria-label={`${isExpanded ? 'Hide' : 'Show'} variations of ${item.p_sku || item.p_title}`}
              className="sk:ml-auto"
            />
          </div>
        </Cell>
        <Cell />
        <Cell>
          <Tags tags={item.tags} />
        </Cell>
        <Cell>{CommonUtility.numberWithCommas(item.total_inventory || 0)}</Cell>
        <Cell />
        <Cell />
      </Row>
      {isExpanded &&
        (variations.isPending ? (
          <PlaceholderRow id={`${item._id}/loading`} />
        ) : (
          variations.data?.map(child => (
            <ItemRow
              key={child._id}
              item={child}
              context={context}
              parentId={item._id}
              siblingIds={variationIds}
            />
          ))
        ))}
    </>
  );
}

export function CatalogRows({
  item,
  context,
  pageItemIds,
}: {
  item: CatalogListItem;
  context: CatalogRowContext;
  /** The page's openable rows, in order. */
  pageItemIds: string[];
}) {
  return item.is_parent ? (
    <ParentRows item={item} context={context} />
  ) : (
    <ItemRow item={item} context={context} siblingIds={pageItemIds} />
  );
}
